import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ArrowLeft, Plus, Pencil, Archive, RotateCcw, Trash2, Search, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";

const ROUTE_TO_CATALOG = {
  "departamentos":              "departments",
  "puestos":                    "positions",
  "categorias-profesionales":   "professional_categories",
  "grupos-cotizacion":          "contribution_groups",
  "tipos-contrato":             "contract_types",
  "jornadas":                   "workday_types",
  "situaciones-laborales":      "employment_statuses",
  "convenios":                  "agreements",
  "centros-coste":              "cost_centers",
};

const CATALOG_DEFS = {
  centers:                  { label: "Centros", singular: "Centro", extraFields: [] /* gestionado en /admin/centros */ },
  departments:              { label: "Departamentos", singular: "Departamento", extraFields: [] },
  positions:                { label: "Puestos", singular: "Puesto", extraFields: [] },
  professional_categories:  { label: "Categorías profesionales", singular: "Categoría", extraFields: [{ key: "agreement_id", label: "Convenio asociado", type: "select_agreement" }] },
  contribution_groups:      { label: "Grupos de cotización", singular: "Grupo", extraFields: [{ key: "code", label: "Código" }] },
  contract_types:           { label: "Tipos de contrato", singular: "Tipo contrato", extraFields: [{ key: "code", label: "Código" }, { key: "is_laboral", label: "Es laboral", type: "switch" }] },
  workday_types:            { label: "Jornadas", singular: "Jornada", extraFields: [{ key: "default_weekly_hours", label: "Horas semanales", type: "number" }] },
  employment_statuses:      { label: "Situaciones laborales", singular: "Situación", extraFields: [{ key: "code", label: "Código" }, { key: "blocks_user_access", label: "Bloquea acceso de usuario", type: "switch" }, { key: "color", label: "Color (hex)", placeholder: "#22c55e" }] },
  agreements:               { label: "Convenios", singular: "Convenio", extraFields: [{ key: "code", label: "Código" }] },
  cost_centers:             { label: "Centros de coste", singular: "Centro de coste", extraFields: [{ key: "code", label: "Código contable" }] },
};

const EMPTY = { name: "", slug: "", description: "", sort_order: 0, active: true };

export default function CatalogPage() {
  const location = useLocation();
  const lastSeg = location.pathname.split("/").filter(Boolean).pop();
  const catalog = ROUTE_TO_CATALOG[lastSeg];
  const navigate = useNavigate();
  const { axiosAuth, user } = useAuth();
  const def = CATALOG_DEFS[catalog];
  const queryParams = new URLSearchParams(location.search);
  const issueFilter = queryParams.get("issue");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [perms, setPerms] = useState({ can_manage: false, can_delete: false });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [agreements, setAgreements] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteText, setDeleteText] = useState("");

  const loadPerms = useCallback(async () => {
    try {
      const res = await axiosAuth().get("/hr-catalogs-permissions");
      setPerms(res.data[catalog] || {});
    } catch { /* */ }
  }, [axiosAuth, catalog]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("q", search);
      if (includeArchived) params.set("include_archived", "true");
      const res = await axiosAuth().get(`/hr-catalogs/${catalog}?${params.toString()}`);
      setItems(res.data || []);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al cargar"); }
    finally { setLoading(false); }
  }, [axiosAuth, catalog, search, includeArchived]);

  const loadAgreements = useCallback(async () => {
    if (catalog !== "professional_categories") return;
    try {
      const res = await axiosAuth().get("/hr-catalogs/agreements?active_only=true");
      setAgreements(res.data || []);
    } catch { /* */ }
  }, [axiosAuth, catalog]);

  useEffect(() => { loadPerms(); }, [loadPerms]);
  useEffect(() => { loadAgreements(); }, [loadAgreements]);
  useEffect(() => { const t = setTimeout(() => load(), 200); return () => clearTimeout(t); }, [load]);

  const openCreate = () => { setForm({ ...EMPTY }); setEditing("new"); };
  const openEdit = (item) => {
    const data = { ...EMPTY, ...item };
    setForm(data);
    setEditing(item.id);
  };

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Nombre obligatorio"); return; }
    try {
      const payload = { ...form };
      if (editing === "new") {
        await axiosAuth().post(`/hr-catalogs/${catalog}`, payload);
        toast.success("Creado");
      } else {
        await axiosAuth().put(`/hr-catalogs/${catalog}/${editing}`, payload);
        toast.success("Guardado");
      }
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
  };

  const toggleActive = async (item) => {
    try {
      await axiosAuth().put(`/hr-catalogs/${catalog}/${item.id}`, { active: !item.active });
      toast.success(item.active ? "Desactivado" : "Activado");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const archive = async (item) => {
    if (!window.confirm(`¿Archivar "${item.name}"? Quedará oculto y no podrá usarse en nuevos registros. Conserva valores en empleados antiguos.`)) return;
    try {
      await axiosAuth().put(`/hr-catalogs/${catalog}/${item.id}/archive`);
      toast.success("Archivado");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const restore = async (item) => {
    try {
      await axiosAuth().put(`/hr-catalogs/${catalog}/${item.id}/restore`);
      toast.success("Restaurado");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const doDelete = async () => {
    if (!deleteConfirm) return;
    if (deleteText !== "ELIMINAR") { toast.error("Escribe ELIMINAR para confirmar"); return; }
    try {
      await axiosAuth().delete(`/hr-catalogs/${catalog}/${deleteConfirm.id}?confirm=ELIMINAR`);
      toast.success("Eliminado definitivamente");
      setDeleteConfirm(null); setDeleteText("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al eliminar"); }
  };

  const filtered = useMemo(() => {
    if (issueFilter === "duplicates") {
      // Agrupar por nombre normalizado y mostrar solo los grupos con >1 item
      const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
      const groups = {};
      items.forEach(it => { const k = norm(it.name); if (k) (groups[k] = groups[k] || []).push(it); });
      const dupIds = new Set();
      Object.values(groups).forEach(arr => { if (arr.length > 1) arr.forEach(x => dupIds.add(x.id)); });
      return items.filter(x => dupIds.has(x.id));
    }
    return items;
  }, [items, issueFilter]);

  if (!def) {
    return <div className="p-4 text-sm text-red-600">Catálogo no reconocido: {catalog}</div>;
  }

  return (
    <div className="space-y-4" data-testid={`catalog-page-${catalog}`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end gap-3 justify-between">
        <div>
          <button onClick={() => navigate("/admin/catalogos-rrhh")} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-1" data-testid="back-link">
            <ArrowLeft className="w-3 h-3" /> Volver al resumen
          </button>
          <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase">{def.label}</h1>
          <p className="text-sm text-slate-500 mt-1">Catálogo maestro · {items.length} elemento{items.length === 1 ? "" : "s"}</p>
        </div>
        {perms.can_manage && (
          <Button onClick={openCreate} className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-xs uppercase tracking-wider font-semibold h-9 px-3" data-testid="create-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuevo {def.singular}
          </Button>
        )}
      </div>

      {/* Filters */}
      {issueFilter === "duplicates" && (
        <div className="flex items-center justify-between gap-3 bg-amber-50 border border-amber-300 rounded-sm px-3 py-2" data-testid="issue-banner-duplicates">
          <div className="flex items-center gap-2 min-w-0">
            <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0" />
            <span className="text-xs font-semibold text-amber-900">Mostrando solo posibles duplicados por nombre normalizado</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={() => navigate(location.pathname)} className="h-7 text-xs rounded-sm" data-testid="clear-issue-filter">Limpiar filtro</Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/catalogos-rrhh")} className="h-7 text-xs rounded-sm" data-testid="back-to-summary">Volver a Catálogos RRHH</Button>
          </div>
        </div>
      )}

      <Card className="border-slate-200 rounded-sm">
        <CardContent className="p-3 flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="relative flex-1 min-w-0 sm:max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar..." className="pl-8 h-8 text-xs rounded-sm border-slate-200" data-testid="search-input" />
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <Switch checked={includeArchived} onCheckedChange={setIncludeArchived} data-testid="include-archived" />
            Incluir archivados
          </label>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200 rounded-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Nombre</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold hidden sm:table-cell">Identificador</TableHead>
                  {def.extraFields.map(f => (
                    <TableHead key={f.key} className="text-xs uppercase tracking-wider font-semibold hidden md:table-cell">{f.label}</TableHead>
                  ))}
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Estado</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={4 + def.extraFields.length} className="text-center py-8 text-slate-400">Cargando...</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={4 + def.extraFields.length} className="text-center py-8 text-slate-400">Sin elementos. Crea el primero.</TableCell></TableRow>
                ) : filtered.map(item => (
                  <TableRow key={item.id} className={`hover:bg-slate-50 ${item.archived ? "opacity-60" : ""}`} data-testid={`row-${item.id}`}>
                    <TableCell className="text-sm font-medium text-slate-800">{item.name}</TableCell>
                    <TableCell className="text-xs text-slate-500 font-mono hidden sm:table-cell">{item.slug}</TableCell>
                    {def.extraFields.map(f => (
                      <TableCell key={f.key} className="text-xs text-slate-600 hidden md:table-cell">
                        {f.type === "switch" ? (item[f.key] ? "Sí" : "No") :
                         f.type === "select_agreement" ? agreements.find(a => a.id === item[f.key])?.name || "—" :
                         item[f.key] ?? "—"}
                      </TableCell>
                    ))}
                    <TableCell>
                      {item.archived ? <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-[10px] rounded-sm">Archivado</Badge> :
                       item.active ? <Badge className="bg-green-100 text-green-700 border border-green-200 text-[10px] rounded-sm">Activo</Badge> :
                       <Badge className="bg-slate-100 text-slate-600 border border-slate-200 text-[10px] rounded-sm">Inactivo</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {perms.can_manage && !item.archived && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => openEdit(item)} className="text-xs h-7" data-testid={`edit-${item.id}`}>
                              <Pencil className="w-3 h-3" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => toggleActive(item)} className="text-xs h-7" title={item.active ? "Desactivar" : "Activar"} data-testid={`toggle-${item.id}`}>
                              {item.active ? <XCircle className="w-3 h-3 text-slate-500" /> : <CheckCircle2 className="w-3 h-3 text-green-600" />}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => archive(item)} className="text-xs h-7 text-amber-700" title="Archivar" data-testid={`archive-${item.id}`}>
                              <Archive className="w-3 h-3" />
                            </Button>
                          </>
                        )}
                        {perms.can_manage && item.archived && (
                          <Button variant="ghost" size="sm" onClick={() => restore(item)} className="text-xs h-7 text-green-700" title="Restaurar" data-testid={`restore-${item.id}`}>
                            <RotateCcw className="w-3 h-3" />
                          </Button>
                        )}
                        {perms.can_delete && (
                          <Button variant="ghost" size="sm" onClick={() => { setDeleteConfirm(item); setDeleteText(""); }} className="text-xs h-7 text-red-700" title="Eliminar definitivo" data-testid={`delete-${item.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit dialog */}
      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="max-w-md rounded-sm" data-testid="edit-dialog">
          <DialogHeader>
            <DialogTitle className="heading-industrial text-lg uppercase">{editing === "new" ? `Nuevo ${def.singular}` : `Editar ${def.singular}`}</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">Define los campos básicos del elemento.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Nombre *</Label>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="rounded-sm h-9 border-slate-200" data-testid="form-name" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Identificador (slug, autogenerado si vacío)</Label>
              <Input value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value})} className="rounded-sm h-9 border-slate-200 font-mono" data-testid="form-slug" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Descripción</Label>
              <Textarea value={form.description || ""} onChange={(e) => setForm({...form, description: e.target.value})} rows={2} className="rounded-sm border-slate-200 text-xs" data-testid="form-description" />
            </div>
            {def.extraFields.map(f => (
              <div key={f.key} className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">{f.label}</Label>
                {f.type === "switch" ? (
                  <div className="flex items-center gap-2">
                    <Switch checked={!!form[f.key]} onCheckedChange={(v) => setForm({...form, [f.key]: v})} data-testid={`form-${f.key}`} />
                    <span className="text-xs text-slate-600">{form[f.key] ? "Sí" : "No"}</span>
                  </div>
                ) : f.type === "number" ? (
                  <Input type="number" value={form[f.key] ?? ""} onChange={(e) => setForm({...form, [f.key]: parseFloat(e.target.value) || 0})} className="rounded-sm h-9 border-slate-200" data-testid={`form-${f.key}`} />
                ) : f.type === "select_agreement" ? (
                  <select value={form[f.key] || ""} onChange={(e) => setForm({...form, [f.key]: e.target.value})}
                    className="w-full rounded-sm h-9 border border-slate-200 text-xs px-2" data-testid={`form-${f.key}`}>
                    <option value="">— Sin convenio —</option>
                    {agreements.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                ) : (
                  <Input value={form[f.key] ?? ""} onChange={(e) => setForm({...form, [f.key]: e.target.value})} placeholder={f.placeholder || ""} className="rounded-sm h-9 border-slate-200" data-testid={`form-${f.key}`} />
                )}
              </div>
            ))}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Orden</Label>
                <Input type="number" value={form.sort_order || 0} onChange={(e) => setForm({...form, sort_order: parseInt(e.target.value) || 0})} className="rounded-sm h-9 border-slate-200" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Activo</Label>
                <div className="flex items-center h-9">
                  <Switch checked={!!form.active} onCheckedChange={(v) => setForm({...form, active: v})} data-testid="form-active" />
                  <span className="text-xs ml-2 text-slate-600">{form.active ? "Activo" : "Inactivo"}</span>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-sm text-xs uppercase">Cancelar</Button>
            <Button onClick={save} className="bg-slate-900 hover:bg-slate-800 rounded-sm text-xs uppercase font-semibold" data-testid="form-submit">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => { setDeleteConfirm(null); setDeleteText(""); }}>
        <DialogContent className="max-w-md rounded-sm" data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="heading-industrial text-lg uppercase text-red-700">Eliminar definitivamente</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">Acción irreversible. Bloqueada si hay referencias.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-red-50 border border-red-200 rounded-sm p-3 text-xs text-red-700">
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
              Vas a eliminar "{deleteConfirm?.name}" del catálogo. Solo se permite si no hay referencias activas. Considera **Archivar** en su lugar.
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider font-semibold">Escribe <span className="font-mono bg-slate-100 px-1">ELIMINAR</span></Label>
              <Input value={deleteText} onChange={(e) => setDeleteText(e.target.value)} className="rounded-sm h-9 border-red-200 font-mono" data-testid="delete-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteConfirm(null); setDeleteText(""); }} className="rounded-sm text-xs uppercase">Cancelar</Button>
            <Button onClick={doDelete} disabled={deleteText !== "ELIMINAR"} className="bg-red-700 hover:bg-red-800 disabled:bg-slate-300 rounded-sm text-xs uppercase font-semibold" data-testid="delete-submit">Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
